import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import { cloneDeep, flatten } from 'lodash';
import moment from 'moment';
import {
  ActivateDeactivateCommands,
  CallForHeatDemandProperties,
  ConsolidatedFeature,
  ConsolidatorDevice,
  DcSensorValueProperties,
  DeviceConfigProperty,
  DeviceHeatingCircuitIdCommands,
  DeviceIds,
  DeviceRoles,
  Feature,
  FeatureNames,
  GatewayDevicesProperties,
  GenericStringValueProperty,
  HeatingConfigurationRegulationMode,
  HeatingConfigurationRegulationProperties,
  heatingDhwPumpStatus,
  HeatingZoneOperatingPrograms,
  HeatingZoneReducedEnergySavingReason,
  IsActiveProperty,
  IsEnabledProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
  OtherRoomCommands,
  OtherRoomProperties,
  OtherRoomsCommands,
  PowerLimitationOverviewProperties,
  PvStringIdLetter,
  RoomCo2Commands,
  RoomCo2Properties,
  RoomCommands,
  RoomProperties,
  RoomsCommands,
  RoomsStatusProperties,
  ScheduleCommands,
  ScheduleProperties,
  TypedFeatureProperties,
  VentilationProgramDetails,
  VentilationQuickSelectionsCommands,
  VentilationQuickSelectionsProperties,
  VitotrolName,
  VitotrolRemoteControllerCommands,
  VitotrolRemoteControllerProperties,
  EssSystemType,
  PowerLimitationType,
  PowerLimitationTooltip,
  SecondaryHeatGeneratorProperty,
} from '../../../projects/client';
import { DHW_SCHEDULE_LEVEL } from '../../features/timemanager/schedule.enums';
import { AppState } from '../app.state';
import { AuthSelectors } from '../auth';
import { RemoteDeviceType } from '../commissioning';
import { ConfigurationSelectors } from '../configuration';
import { DevicesSelectors } from '../devices/devices.selectors';
import { HEATING_ZONE_OPERATING_PROGRAM, RichDevice } from '../devices/devices.state';
import { DeviceTypes, GatewaySelectors, GatewayState, RemoteDeviceDetails } from '../gateway';
import { HemsSelectors } from '../hems/hems.selectors';
import {
  Gateway,
  VentilationQuickSelectionsDetails,
} from '../installation-details/installation-details.model';
import { InstallationDetailsSelectors } from '../installation-details/installation-details.selectors';
import { getFhtMainFromChannel, isRemoteDeviceOfType } from '../remote-device';
import { isReady } from '../_common';
import { InstallationFeaturesHelpers } from './installation-features.helpers';
import {
  AddActorConstraints,
  Co2LevelStatus,
  PvStringObject,
  RoomActor,
  RoomTypes,
} from './installation-features.models';
import { featuresAdapter } from './installation-features.reducer';
import { InstallationFeaturesState } from './installation-features.state';

export namespace InstallationFeaturesSelectors {
  export const gatewayState = (state: AppState): GatewayState => state.gateway;

  export const {
    selectIds,
    selectEntities,
    selectAll,
    selectTotal,
  } = featuresAdapter.getSelectors();

  export const installationFeatures = (state: AppState): InstallationFeaturesState =>
    state.installationFeatures;

  export const selectHemsFeaturesPresent = (state: AppState): boolean =>
    Object.keys(state.installationFeatures.entities).some((key) => key.includes('HEMS'));

  export const selectFeaturesLoading = createSelector(
    installationFeatures,
    (state: InstallationFeaturesState) => state.getFeaturesLoading
  );

  export const selectUpdateFeatureLoading = createSelector(
    installationFeatures,
    (state: InstallationFeaturesState) => state.updateFeatureLoading
  );

  export const selectFeaturesForAllGatewayLoading = createSelector(
    installationFeatures,
    (state: InstallationFeaturesState) => state.getFeaturesForAllGatwewaysLoading
  );

  export const selectFeaturesError = createSelector(
    installationFeatures,
    (state: InstallationFeaturesState) => state.getFeaturesError
  );

  export const selectReplaceGatewayFormData = createSelector(
    installationFeatures,
    (state: InstallationFeaturesState) => state.replaceGateWayFormData
  );

  export function selectInstallationFeatureByUri<TProps, TCommands>(
    uri: string
  ): MemoizedSelector<
    AppState,
    Feature<TProps, TCommands>,
    (s1: InstallationFeaturesState) => Feature<TProps, TCommands>
  > {
    return createSelector(
      installationFeatures,
      (state: InstallationFeaturesState) => state.entities[uri] as Feature<TProps, TCommands>
    );
  }

  export function selectInstallationHemsFeature<TProps, TCommands>(
    featureName: string
  ): MemoizedSelector<
    AppState,
    Feature<TProps, TCommands>,
    (
      s1: InstallationFeaturesState,
      s2: string,
      s3: number,
      s4: string
    ) => Feature<TProps, TCommands>
  > {
    return createSelector(
      installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      HemsSelectors.selectHemsGatewaySerialIfNoError,
      (
        state: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        hemsGatewaySerial: string
      ) => {
        const uri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
          featuresBaseUri,
          featureName,
          hemsGatewaySerial,
          stateInstallationId,
          DeviceIds.Hems
        );

        return state.entities[uri] as Feature<TProps, TCommands>;
      }
    );
  }

  export function getInstallationDeviceFeature<TProps, TCommands>(
    featureName: string,
    deviceId: string,
    featuresBaseUri: string,
    stateInstallationId: number,
    stateGatewaySerial: Gateway,
    state: InstallationFeaturesState
  ): Feature<TProps, TCommands> {
    const uri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
      featuresBaseUri,
      featureName,
      stateGatewaySerial?.serial,
      stateInstallationId,
      deviceId
    );

    return state.entities[uri] as Feature<TProps, TCommands>;
  }

  export function selectInstallationDeviceFeature<TProps, TCommands>(
    featureName: string,
    deviceId?: string,
    gatewaySerial?: string
  ): MemoizedSelector<
    AppState,
    Feature<TProps, TCommands>,
    (
      s1: InstallationFeaturesState,
      s2: string,
      s3: number,
      s4: Gateway,
      s5: RichDevice
    ) => Feature<TProps, TCommands>
  > {
    return createSelector(
      installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      DevicesSelectors.getSelectedDevice,
      (
        state: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        stateDevice: RichDevice
      ) => {
        const uri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
          featuresBaseUri,
          featureName,
          gatewaySerial ?? stateGatewaySerial?.serial,
          stateInstallationId,
          // if we pass null, we want to select gw feature, if we ommit deviceId, we can try selected
          deviceId !== undefined ? deviceId : stateDevice?.id
        );

        return state.entities[uri] as Feature<TProps, TCommands>;
      }
    );
  }

  export function selectAllInstallationDeviceRemoteFeatures<TProps, TCommands>(
    featureName: string | RegExp,
    deviceId?: string,
    isHeatingCircuit?: boolean
  ): MemoizedSelector<
    AppState,
    Feature<TProps, TCommands>[],
    DefaultProjectorFn<Feature<TProps, TCommands>[]>
  > {
    return createSelector(installationFeatures, (state: InstallationFeaturesState) => {
      const stateEntitieKeys = Object.keys(state.entities);

      let selectedDevices: Feature<
        VitotrolRemoteControllerProperties,
        VitotrolRemoteControllerCommands
      >[] = [];
      stateEntitieKeys.forEach((key) => {
        if (typeof featureName === 'string' && !isHeatingCircuit) {
          if (key.includes(featureName)) {
            selectedDevices.push(
              state.entities[key] as Feature<
                VitotrolRemoteControllerProperties,
                VitotrolRemoteControllerCommands
              >
            );
          }
        } else if (typeof featureName === 'object' && isHeatingCircuit) {
          if (featureName.test(key.substring(key.indexOf(FeatureNames.circuits)))) {
            selectedDevices.push(
              state.entities[key] as Feature<
                VitotrolRemoteControllerProperties,
                VitotrolRemoteControllerCommands
              >
            );
          }
        }
      });

      return selectedDevices.filter((dev) =>
        dev?.commands?.setZigbeeController?.params?.deviceId?.constraints?.enum?.includes(deviceId)
      ) as Feature<TProps, TCommands>[];
    });
  }
  export function selectInstallationDeviceFeatureForHeatingDevice<TProps, TCommands>(
    featureName: string,
    gatewaySerial?: string
  ): MemoizedSelector<
    AppState,
    Feature<TProps, TCommands>,
    (
      s1: InstallationFeaturesState,
      s2: string,
      s3: number,
      s4: Gateway,
      s5: RichDevice
    ) => Feature<TProps, TCommands>
  > {
    return createSelector(
      installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      DevicesSelectors.getHeatingDevice(),
      (
        state: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        heatingDevice: RichDevice
      ) => {
        const uri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
          featuresBaseUri,
          featureName,
          gatewaySerial ?? stateGatewaySerial?.serial,
          stateInstallationId,
          heatingDevice?.id
        );

        return state?.entities[uri] as Feature<TProps, TCommands>;
      }
    );
  }

  export function selectInstallationDeviceFeatureByDeviceType<TProps, TCommands>(
    featureName: string,
    deviceType: DeviceTypes,
    gatewaySerial?: string
  ): MemoizedSelector<
    AppState,
    Feature<TProps, TCommands>,
    (
      s1: InstallationFeaturesState,
      s2: string,
      s3: number,
      s4: Gateway,
      s5: RichDevice
    ) => Feature<TProps, TCommands>
  > {
    return createSelector(
      installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      DevicesSelectors.getDeviceByType(deviceType),
      (
        state: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        deviceOfSpecifiedType: RichDevice
      ) => {
        const uri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
          featuresBaseUri,
          featureName,
          gatewaySerial ?? stateGatewaySerial?.serial,
          stateInstallationId,
          deviceOfSpecifiedType?.id
        );

        return state?.entities[uri] as Feature<TProps, TCommands>;
      }
    );
  }

  export function isInstallationDeviceFeatureReady(
    featureName: string,
    deviceId?: string,
    gatewaySerial?: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        featureName,
        deviceId,
        gatewaySerial
      ),
      (feature: Feature<unknown, unknown>) => isReady(feature)
    );
  }

  export function selectInstallationGatewayFeature<TProps, TCommands>(
    featureName: string,
    gatewaySerial?: string
  ): MemoizedSelector<
    AppState,
    Feature<TProps, TCommands>,
    (
      s1: InstallationFeaturesState,
      s2: string,
      s3: number,
      s4: Gateway
    ) => Feature<TProps, TCommands>
  > {
    return createSelector(
      installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),

      (
        state: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway
      ) => {
        const uri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
          featuresBaseUri,
          featureName,
          gatewaySerial ?? stateGatewaySerial?.serial,
          stateInstallationId
        );

        return state.entities[uri] as Feature<TProps, TCommands>;
      }
    );
  }

  export function selectInstallationGatewayFeatureByDeviceId<TProps, TCommands>(
    featureName: string,
    deviceId: string,
    gatewaySerial?: string
  ): MemoizedSelector<
    AppState,
    Feature<TProps, TCommands>,
    (s1: InstallationFeaturesState) => Feature<TProps, TCommands>
  > {
    return createSelector(installationFeatures, (state: InstallationFeaturesState) => {
      if (state.entities) {
        const features = Object.values(state.entities).filter(
          (feature) =>
            (feature as Feature<TProps, TCommands>).feature === featureName &&
            (gatewaySerial
              ? (feature as Feature<TProps, TCommands>).gatewayId === gatewaySerial
              : true)
        ) as Feature<TProps, TCommands>[];

        return features.find((feature) => feature.deviceId === deviceId);
      }
    });
  }

  export function selectInstallationTCUFeature<TProps, TCommands>(
    featureName: string,
    gatewaySerial: string
  ): MemoizedSelector<AppState, Feature<TProps, TCommands>> {
    return createSelector(
      installationFeatures,
      selectGatewayDeviceIdWithSerial(gatewaySerial),
      (state: InstallationFeaturesState, deviceId: string) => {
        const feature = Object.values(state.entities).find(
          (feature) =>
            (feature as Feature<TProps, TCommands>).feature === featureName &&
            (feature as Feature<TProps, TCommands>).gatewayId === gatewaySerial &&
            (feature as Feature<TProps, TCommands>).deviceId === deviceId
        ) as Feature<TProps, TCommands>;

        return feature;
      }
    );
  }

  export function selectGatewayDeviceIdWithSerial(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string> {
    return createSelector(DevicesSelectors.selectAllDevices, (devices: RichDevice[]) => {
      return devices.find(
        (device: RichDevice) =>
          device.gatewaySerial === gatewaySerial &&
          device.roles.includes(DeviceRoles.GatewayConfiguration)
      ).id;
    });
  }

  export function getCallForHeatHeatingZoneOperatingProgramName(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(FeatureNames.callForHeatDemand),
      (callForHeatDemandFeature: Feature<CallForHeatDemandProperties, never>) => {
        const isHeating = !!callForHeatDemandFeature?.properties?.value.value;

        return isHeating
          ? HEATING_ZONE_OPERATING_PROGRAM.Heating
          : HEATING_ZONE_OPERATING_PROGRAM.Idle;
      }
    );
  }

  export function getCircuitHeatingZoneOperatingProgramName(
    circuitId: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.circuitActiveProgram(circuitId)
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.circuitReducedEnergySaving(circuitId)
      ),
      (
        programsActiveFeature: Feature<IsStringValueProperty, never>,
        reducedEnergySavingFeature: Feature<GenericStringValueProperty, never>
      ) => {
        const programsActiveValue = programsActiveFeature?.properties?.value?.value;
        const reducedEnergySavingReasonValue = reducedEnergySavingFeature?.properties?.reason.value;

        const isIdle = programsActiveValue === HeatingZoneOperatingPrograms.NoDemand;
        const isIdleReducedEnergySaving =
          programsActiveValue === HeatingZoneOperatingPrograms.ReducedEnergySaving &&
          reducedEnergySavingReasonValue === HeatingZoneReducedEnergySavingReason.NoDemand;

        if (isIdle || isIdleReducedEnergySaving) {
          return HEATING_ZONE_OPERATING_PROGRAM.Idle;
        }

        const isHeating = programsActiveValue === HeatingZoneOperatingPrograms.TTContact;
        if (isHeating) {
          return HEATING_ZONE_OPERATING_PROGRAM.Heating;
        }

        const isStandby = programsActiveValue === HeatingZoneOperatingPrograms.Standby;

        if (isStandby) {
          return HEATING_ZONE_OPERATING_PROGRAM.Standby;
        }

        return HEATING_ZONE_OPERATING_PROGRAM.Standby;
      }
    );
  }

  export function getHeatingNoiseReductionMode(
    deviceId?: string,
    gatewaySerial?: string
  ): MemoizedSelector<AppState, string | undefined, DefaultProjectorFn<string | undefined>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        TypedFeatureProperties<boolean>,
        never
      >(FeatureNames.heatingNoiseReduction, deviceId, gatewaySerial),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        TypedFeatureProperties<string>,
        never
      >(FeatureNames.heatingNoiseReductionOperatingState, deviceId, gatewaySerial),
      (noiseReduction, operatingProgram) => {
        const caseReductionAbsent = !noiseReduction;
        const caseBothFeaturesPresent =
          noiseReduction?.properties?.active?.value && operatingProgram?.properties?.level?.value;
        const caseReductionFalsy = noiseReduction?.properties?.active?.value === false;

        const noiseReductionDefault = 'off';

        if (caseReductionAbsent) {
          return undefined;
        } else if (caseBothFeaturesPresent) {
          return operatingProgram.properties.level.value;
        } else if (caseReductionFalsy) {
          return noiseReductionDefault;
        } else {
          return noiseReductionDefault;
        }
      }
    );
  }

  export function getFuelVolume(): MemoizedSelector<AppState, number, DefaultProjectorFn<number>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        TypedFeatureProperties<number>,
        never
      >(FeatureNames.configurationFuelCapacity),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        TypedFeatureProperties<number>,
        never
      >(FeatureNames.heatingDeviceFuelCurrent),
      (capacity, volume) =>
        capacity.properties.value?.value
          ? Math.round(
              ((volume.properties.value?.value ?? 0) / capacity.properties.value?.value) * 100
            )
          : 0
    );
  }

  export function selectDhwStatus(): MemoizedSelector<
    AppState,
    DHW_SCHEDULE_LEVEL,
    DefaultProjectorFn<DHW_SCHEDULE_LEVEL>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<heatingDhwPumpStatus, never>(
        FeatureNames.dhwPumpsPrimary
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<heatingDhwPumpStatus, never>(
        FeatureNames.dhwPumpsSecondary
      ),
      (dhwPumpsPrimary, dhwPumpsSecondary) => {
        const primaryDhwValue = isReady(dhwPumpsPrimary)
          ? dhwPumpsPrimary?.properties?.status?.value
          : undefined;
        const secondaryDhwValue = isReady(dhwPumpsSecondary)
          ? dhwPumpsSecondary?.properties?.status?.value
          : undefined;

        if (
          primaryDhwValue === DHW_SCHEDULE_LEVEL.On ||
          secondaryDhwValue === DHW_SCHEDULE_LEVEL.On
        ) {
          return DHW_SCHEDULE_LEVEL.On;
        } else if (
          primaryDhwValue === DHW_SCHEDULE_LEVEL.Off ||
          secondaryDhwValue === DHW_SCHEDULE_LEVEL.Off
        ) {
          return DHW_SCHEDULE_LEVEL.Off;
        } else {
          return undefined;
        }
      }
    );
  }

  export function ventilationDiagnosticInformationVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.ventilationHeatExchangerFrostprotection
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.preHeaterStatistics
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(FeatureNames.filterRuntime),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(FeatureNames.serviceTimeBased),
      (
        isHeatExchangerfrostProtectionFeatureReady,
        isPreHeaterStatisticsFeatureReady,
        isFilterRuntimeFeatureReady,
        isHeatingServiceTimeBasedFeatureReady
      ) => {
        return (
          isHeatExchangerfrostProtectionFeatureReady ||
          isPreHeaterStatisticsFeatureReady ||
          isFilterRuntimeFeatureReady ||
          isHeatingServiceTimeBasedFeatureReady
        );
      }
    );
  }

  export function selectFeaturesForVentilationQuickSelectionsVisibility(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.ventilationQuickmodesSilent
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.ventilationQuickmodesForcedLevelFour
      ),
      (
        isVentilationQuickmodesSilentFeatureReady,
        isVentilationQuickmodesForcedLevelFourFeatureReady
      ) => {
        return (
          isVentilationQuickmodesSilentFeatureReady ||
          isVentilationQuickmodesForcedLevelFourFeatureReady
        );
      }
    );
  }

  export function getVentilationQuickSelectionsDetails(): MemoizedSelector<
    AppState,
    VentilationQuickSelectionsDetails,
    DefaultProjectorFn<VentilationQuickSelectionsDetails>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        VentilationQuickSelectionsProperties,
        VentilationQuickSelectionsCommands
      >(FeatureNames.ventilationQuickmodesSilent),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        VentilationQuickSelectionsProperties,
        VentilationQuickSelectionsCommands
      >(FeatureNames.ventilationQuickmodesForcedLevelFour),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        VentilationQuickSelectionsProperties,
        VentilationQuickSelectionsCommands
      >(FeatureNames.ventilationQuickmodesTemporaryShutdown),
      (silentProgramFeature, intensiveProgramFeature, temporaryShutdownFeature) => {
        if (silentProgramFeature?.properties?.active?.value) {
          return {
            active: silentProgramFeature,
            defaultRuntime: silentProgramFeature.properties.defaultRuntime.value,
          };
        } else if (intensiveProgramFeature?.properties?.active?.value) {
          return {
            active: intensiveProgramFeature,
            defaultRuntime: intensiveProgramFeature.properties.defaultRuntime.value,
          };
        } else if (temporaryShutdownFeature?.properties?.active?.value) {
          return {
            active: temporaryShutdownFeature,
            defaultRuntime: temporaryShutdownFeature.properties.defaultRuntime.value,
          };
        } else {
          return {
            active: null,
            defaultRuntime: silentProgramFeature?.properties?.defaultRuntime?.value,
          };
        }
      }
    );
  }

  export function isAnySpfFeatureReady(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(FeatureNames.heatingSpfTotal),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.heatingSpfHeating
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(FeatureNames.heatingSpfDhw),
      (
        isHeatingSpfTotalFeatureReady,
        isHeatingSpfHeatingFeatureReady,
        isHeatingSpfDhwFeatureReady
      ) => {
        return (
          isHeatingSpfTotalFeatureReady ||
          isHeatingSpfHeatingFeatureReady ||
          isHeatingSpfDhwFeatureReady
        );
      }
    );
  }

  export function shouldDispalyHeatingPanel(
    circuitId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.circuitHeatingSchedule(circuitId)
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.circuitSingleFamilyHolidayProgram
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.circuitSingleFamilyHolidayAtHome
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.flowLimitTemperature(circuitId)
      ),
      (
        isHeatingScheduleFeatureReady,
        isHolidayFeatureReady,
        isHolidayAtHomeFeatureReady,
        isFlowLimitTemperatureFeatureReady
      ): boolean =>
        isHeatingScheduleFeatureReady ||
        isHolidayFeatureReady ||
        isHolidayAtHomeFeatureReady ||
        isFlowLimitTemperatureFeatureReady
    );
  }

  export function isServiceTimeBasedOrBurnerBasedAvailable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(FeatureNames.serviceTimeBased),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.serviceBurnerBased
      ),
      (isServiceTimeBasedFeatureReady, isServiceBurnerBasedFeatureReady): boolean =>
        isServiceTimeBasedFeatureReady || isServiceBurnerBasedFeatureReady
    );
  }

  export function getVentilationProgramsDetails(): MemoizedSelector<
    AppState,
    VentilationProgramDetails[],
    DefaultProjectorFn<VentilationProgramDetails[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        ScheduleProperties,
        ScheduleCommands
      >(FeatureNames.ventilationSchedule),
      (ventilationScheduleFeature) => {
        const programs =
          ventilationScheduleFeature?.commands?.setSchedule?.params?.newSchedule?.constraints
            ?.modes;
        if (programs && isReady(ventilationScheduleFeature)) {
          const schedulePrograms = ventilationScheduleFeature.commands.setSchedule.params.newSchedule.constraints.modes.map(
            (mode) => ({
              name: mode,
            })
          );
          schedulePrograms.push({
            name:
              ventilationScheduleFeature.commands.setSchedule.params.newSchedule.constraints
                .defaultMode,
          });

          return schedulePrograms.map((program) => {
            return {
              name: program.name,
              css: 'program-' + program.name.replace(/\//gm, '-'),
            };
          });
        }
      }
    );
  }

  export function isVicareThermostatMode(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        HeatingConfigurationRegulationProperties,
        never
      >(FeatureNames.configurationRegulation),
      (configurationRegulation) =>
        isReady(configurationRegulation) &&
        (configurationRegulation.properties.mode.value ===
          HeatingConfigurationRegulationMode.DeltaTRoomOnOff ||
          configurationRegulation.properties.mode.value ===
            HeatingConfigurationRegulationMode.DeltaTRoomModulation)
    );
  }

  export function getOffsetDeviceTime(): MemoizedSelector<
    AppState,
    Date,
    DefaultProjectorFn<Date>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<IsNumberValueProperty, {}>(
        FeatureNames.heatingDeviceTimeOffset
      ),
      (heatingDeviceTimeOffset) => {
        const offsetValue = heatingDeviceTimeOffset?.properties?.value?.value;
        if (
          !isReady(heatingDeviceTimeOffset) ||
          offsetValue === null ||
          offsetValue === undefined
        ) {
          return null;
        }

        const date = moment.utc().add(offsetValue, 'm').toDate();

        return date;
      }
    );
  }

  export function srcMenuItemVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.rooms,
        DeviceIds.RoomControl
      ),
      InstallationFeaturesSelectors.isVicareThermostatMode(),
      (isRoomsFeatureReady, isVicareThermostatModeOn: boolean) => {
        return isRoomsFeatureReady && !isVicareThermostatModeOn;
      }
    );
  }

  export function selectIsLocalVsDeviceTimeMismatch(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.getOffsetDeviceTime(),
      (offsetDeviceTime: Date): boolean => {
        if (!offsetDeviceTime) {
          return false;
        }

        const dateDevice = moment(offsetDeviceTime);
        const dateNow = moment().utcOffset(0, true).local();

        const differenceInMinutes = dateDevice.diff(dateNow, 'minutes');

        return Math.abs(differenceInMinutes) > 10;
      }
    );
  }

  export function holidayModeMenuItemVisible(
    firstEnabledCircuitId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.circuitSingleFamilyHolidayProgram
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.circuitHolidayProgram(firstEnabledCircuitId)
      ),
      InstallationDetailsSelectors.hasRoomControlGivenRoles([DeviceRoles.SrcHoliday]),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        RoomsStatusProperties,
        ActivateDeactivateCommands
      >(FeatureNames.roomStatus, DeviceIds.RoomControl),
      (
        isHolidaySingleFamilyFeatureReady: boolean,
        isHolidayMultiFamilyFeatureReady: boolean,
        isSrcHolidayRole: boolean,
        roomStatus: Feature<RoomsStatusProperties, ActivateDeactivateCommands>
      ) => {
        if (!isSrcHolidayRole && roomStatus?.properties?.active?.value) {
          return false;
        }

        return isHolidaySingleFamilyFeatureReady || isHolidayMultiFamilyFeatureReady;
      }
    );
  }

  export function controlUnitsMenuItemVisible(
    boilerSerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationDetailsSelectors.isPlusOrProPackage(boilerSerial),
      AuthSelectors.isRsaUser(),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.deviceBusTopology
      ),
      (proOrPlusPackage, isRsa, busTopologyFeature: boolean) =>
        busTopologyFeature && (proOrPlusPackage || isRsa)
    );
  }

  export function controlMenuVisible(
    firstEnabledCircuitId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.holidayModeMenuItemVisible(firstEnabledCircuitId),
      InstallationFeaturesSelectors.srcMenuItemVisible(),
      (holidayModeVisible, srcVisible) => holidayModeVisible || srcVisible
    );
  }

  export function installationDetailsMenuVisible(
    gatewaySerial: string,
    boilerSerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationDetailsSelectors.hydraulicSchemeMenuItemVisible(gatewaySerial),
      InstallationFeaturesSelectors.controlUnitsMenuItemVisible(boilerSerial),
      InstallationDetailsSelectors.getGatewayStatus(gatewaySerial),
      (schemeVisible, controlUnitsVisible, gatewayStatus) =>
        (schemeVisible || controlUnitsVisible) && !gatewayStatus.offline
    );
  }

  export function getEnabledRoomsIds(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(InstallationFeaturesSelectors.getRoomsFeature(), (roomsFeature) => {
      const enabledRooms = roomsFeature?.properties?.enabled?.value;

      return enabledRooms || [];
    });
  }

  export function getCo2LevelStatus(): MemoizedSelector<
    AppState,
    Co2LevelStatus,
    DefaultProjectorFn<Co2LevelStatus>
  > {
    return createSelector(
      installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      InstallationFeaturesSelectors.getEnabledRoomsIds(),
      (
        state: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        enabledRoomsIds: string[]
      ) => {
        const criticalPpmThreshold = 1500;
        let status = Co2LevelStatus.Good;

        const ppmRoomValues: { concentration: number; threshold: number }[] = [];

        enabledRoomsIds.forEach((roomId) => {
          const uri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
            featuresBaseUri,
            FeatureNames.roomSensorsCo2(roomId),
            stateGatewaySerial?.serial,
            stateInstallationId,
            DeviceIds.RoomControl
          );
          const sensorCo2 = state.entities[uri] as Feature<DcSensorValueProperties, never>;
          const roomCo2 = state.entities[uri] as Feature<RoomCo2Properties, RoomCo2Commands>;

          const roomPpmConcentration = sensorCo2?.properties?.value?.value;
          const roomPpmThreshold = roomCo2?.properties?.concentration?.value;

          if (!roomPpmConcentration || !roomPpmThreshold) {
            return;
          }

          ppmRoomValues.push({
            concentration: roomPpmConcentration,
            threshold: roomPpmThreshold,
          });
        });

        const isAboveThreshold = ppmRoomValues.some((roomValues) => {
          return roomValues.concentration > roomValues.threshold;
        });

        const isAboveCriticalThreshold = ppmRoomValues.some((roomValues) => {
          return roomValues.concentration > criticalPpmThreshold;
        });

        if (isAboveCriticalThreshold) {
          status = Co2LevelStatus.CriticalThreshold;
        } else if (isAboveThreshold) {
          status = Co2LevelStatus.AboveThreshold;
        }

        return status;
      }
    );
  }

  export function getOtherRoomsFeature(): MemoizedSelector<
    AppState,
    Feature<IsEnabledProperty, OtherRoomsCommands>,
    DefaultProjectorFn<Feature<IsEnabledProperty, OtherRoomsCommands>>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        IsEnabledProperty,
        OtherRoomsCommands
      >(FeatureNames.otherRooms, DeviceIds.RoomControl),
      (otherRoomsFeature) => otherRoomsFeature
    );
  }

  export function getOtherRoomsFeatures(): MemoizedSelector<
    AppState,
    Feature<OtherRoomProperties, OtherRoomCommands>[],
    DefaultProjectorFn<Feature<OtherRoomProperties, OtherRoomCommands>[]>
  > {
    return createSelector(
      installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      InstallationFeaturesSelectors.getOtherRoomsFeature(),
      (
        state: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        otherRoomsFeature
      ) => {
        let otherRooms: Feature<OtherRoomProperties, OtherRoomCommands>[] = [];

        otherRooms = otherRoomsFeature?.properties?.enabled?.value
          .map((roomId: string) => {
            const uri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
              featuresBaseUri,
              FeatureNames.otherRoomDetails(roomId),
              stateGatewaySerial?.serial,
              stateInstallationId,
              DeviceIds.RoomControl
            );

            return state.entities[uri] as Feature<OtherRoomProperties, OtherRoomCommands>;
          })
          .filter((feature) => isReady(feature));

        return otherRooms ?? [];
      }
    );
  }

  export function getRoomsFeature(): MemoizedSelector<
    AppState,
    Feature<IsEnabledProperty, RoomsCommands>,
    DefaultProjectorFn<Feature<IsEnabledProperty, RoomsCommands>>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        IsEnabledProperty,
        RoomsCommands
      >(FeatureNames.rooms, DeviceIds.RoomControl),
      (roomsFeature) => roomsFeature
    );
  }

  export function getRoomsFeatures(): MemoizedSelector<
    AppState,
    Feature<RoomProperties, RoomCommands>[],
    DefaultProjectorFn<Feature<RoomProperties, RoomCommands>[]>
  > {
    return createSelector(
      installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      InstallationFeaturesSelectors.getRoomsFeature(),
      (
        state: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        roomsFeature
      ) => {
        const rooms: Feature<RoomProperties, RoomCommands>[] = [];

        roomsFeature?.properties?.enabled?.value?.forEach((roomId) => {
          const uri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
            featuresBaseUri,
            FeatureNames.roomDetails(roomId),
            stateGatewaySerial?.serial,
            stateInstallationId,
            DeviceIds.RoomControl
          );
          const roomFeature = state.entities[uri] as Feature<RoomProperties, RoomCommands>;

          if (isReady(roomFeature)) {
            rooms.push(roomFeature);
          }
        });

        return rooms;
      }
    );
  }

  export function getRoomsCompatibileWithFhtChannels(): MemoizedSelector<
    AppState,
    Feature<RoomProperties, RoomCommands>[],
    DefaultProjectorFn<Feature<RoomProperties, RoomCommands>[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      InstallationFeaturesSelectors.getRoomsFeatures(),
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        rooms: Feature<RoomProperties, RoomCommands>[]
      ): Feature<RoomProperties, RoomCommands>[] => {
        return rooms.filter((entry) => {
          const roomActors = entry.properties.actors.value.map((actor) => actor.deviceId);

          const isCompatibileRoom = roomActors.some((actor) => {
            const isCs = GatewaySelectors.getIsDeviceOfType(
              actor,
              RemoteDeviceType.CS,
              gatewayDevices
            );
            const isCo2 = GatewaySelectors.getIsDeviceOfType(
              actor,
              RemoteDeviceType.AIR_QUALITY_SENSOR,
              gatewayDevices
            );

            return isCs || isCo2;
          });

          return isCompatibileRoom;
        });
      }
    );
  }

  export function getRoomsAndOtherRoomsNames(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.getOtherRoomsFeatures(),
      InstallationFeaturesSelectors.getRoomsFeatures(),
      (
        otherRoomsFeatures: Feature<OtherRoomProperties, OtherRoomCommands>[],
        roomsFeatures: Feature<RoomProperties, RoomCommands>[]
      ) => {
        const roomsAndOtherRooms = [...otherRoomsFeatures, ...roomsFeatures];

        const result = [];

        roomsAndOtherRooms.forEach((room) => {
          const roomName = room?.properties?.name?.value;
          if (roomName) {
            result.push(roomName);
          }
        });

        return result;
      }
    );
  }

  export function getNotAssignedHCsToOtherRooms(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.getOtherRoomsFeatures(),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(FeatureNames.circuits),

      (
        otherRoomsFeatures: Feature<OtherRoomProperties, OtherRoomCommands>[],
        heatingCircuitsFeature: Feature<IsEnabledProperty, never>
      ) => {
        const enabledHeatingCircuits = cloneDeep(
          heatingCircuitsFeature?.properties?.enabled?.value
        );

        const result = enabledHeatingCircuits || [];

        if (result?.length) {
          otherRoomsFeatures.map((feature: Feature<OtherRoomProperties, OtherRoomCommands>) => {
            if (feature?.properties?.heatingCircuit) {
              result.map((heatingCircuit: string, index: number) => {
                if (heatingCircuit === feature.properties.heatingCircuit?.value?.toString()) {
                  result.splice(index, 1);
                }
              });
            }
          });
        }

        return result;
      }
    );
  }

  export function getRoomsWithNoActors(): MemoizedSelector<
    AppState,
    Feature<RoomProperties, RoomCommands>[],
    DefaultProjectorFn<Feature<RoomProperties, RoomCommands>[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.getRoomsFeatures(),
      (roomsFeatures: Feature<RoomProperties, RoomCommands>[]) =>
        roomsFeatures.filter(
          (feature) => isReady(feature) && !feature?.properties?.actors?.value?.length
        )
    );
  }

  export function getRoomsWithActors(): MemoizedSelector<
    AppState,
    Feature<RoomProperties, RoomCommands>[],
    DefaultProjectorFn<Feature<RoomProperties, RoomCommands>[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.getRoomsFeatures(),
      (roomsFeatures: Feature<RoomProperties, RoomCommands>[]) =>
        roomsFeatures.filter(
          (feature) => isReady(feature) && feature?.properties?.actors?.value?.length
        )
    );
  }

  export function isAnyRoomWithDevice(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.getRoomsWithActors(),
      (roomsWithActors: Feature<RoomProperties, RoomCommands>[]) => !!roomsWithActors.length
    );
  }

  export function isTheOnlyAssignedDevice(
    deviceId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.getRoomsWithActors(),
      (roomsWithActors: Feature<RoomProperties, RoomCommands>[]) => {
        if (roomsWithActors.length !== 1) {
          return false;
        }

        const roomActors = roomsWithActors[0].properties.actors.value.map(
          (entry) => entry.deviceId
        );

        return roomActors.length === 1 && roomActors.includes(deviceId);
      }
    );
  }

  export function isTheOnlyRoomWithDevices(
    room: Feature<RoomProperties, RoomCommands>
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.getRoomsWithActors(),
      (roomsWithActors: Feature<RoomProperties, RoomCommands>[]) =>
        roomsWithActors.length === 1 && room.feature === roomsWithActors[0].feature
    );
  }

  export function isAddRoomDisabled(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.getRoomsFeature(),
      (roomsFeature: Feature<IsEnabledProperty, RoomsCommands>) =>
        !roomsFeature?.commands?.add?.isExecutable || false
    );
  }

  export function getDeviceRoom(
    deviceId: string
  ): MemoizedSelector<
    AppState,
    Feature<RoomProperties, RoomCommands>,
    DefaultProjectorFn<Feature<RoomProperties, RoomCommands>>
  > {
    return createSelector(
      InstallationFeaturesSelectors.getRoomsWithActors(),
      (roomsWithActors: Feature<RoomProperties, RoomCommands>[]) => {
        let deviceRoom: Feature<RoomProperties, RoomCommands> = null;
        roomsWithActors.some((room) => {
          const foundDevice = (room?.properties?.actors?.value || []).find(
            (actor) => actor.deviceId === deviceId
          );
          if (foundDevice) {
            deviceRoom = room;

            return true;
          }

          return false;
        });

        return deviceRoom;
      }
    );
  }

  export function getDeviceRoomFeature(
    deviceId: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      InstallationFeaturesSelectors.getDeviceRoom(deviceId),
      (deviceRoom: Feature<RoomProperties, RoomCommands>) => deviceRoom.feature
    );
  }

  export function canDeleteClimateSensor(
    deviceId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.getDeviceRoom(deviceId),
      InstallationFeaturesSelectors.canRemoveClimateSensor(deviceId),
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      (
        deviceRoom: Feature<RoomProperties, RoomCommands>,
        canRemoveClimateSensor: boolean,
        gatewayDevices: Feature<GatewayDevicesProperties, never>
      ) => {
        const isClimateSensor = GatewaySelectors.getIsDeviceOfType(
          deviceId,
          RemoteDeviceType.CS,
          gatewayDevices
        );

        return !isClimateSensor || !deviceRoom ? true : canRemoveClimateSensor;
      }
    );
  }

  export function canRemoveClimateSensor(
    deviceId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.getDeviceRoom(deviceId),
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      (
        roomFeature: Feature<RoomProperties, RoomCommands>,
        gatewayDevices: Feature<GatewayDevicesProperties, never>
      ) => {
        if (isReady(roomFeature)) {
          const actors = roomFeature?.properties?.actors?.value;
          if (actors && actors.length) {
            const devices = GatewaySelectors.getZigbeeDevices(gatewayDevices).devices;

            const isClimateSensor = devices.some(
              (device) =>
                device &&
                device.id === deviceId &&
                isRemoteDeviceOfType(device.roles, RemoteDeviceType.CS)
            );
            const isCO2Sensor = devices.some(
              (device) =>
                device &&
                device.id === deviceId &&
                isRemoteDeviceOfType(device.roles, RemoteDeviceType.AIR_QUALITY_SENSOR)
            );
            if (isClimateSensor || isCO2Sensor) {
              const channelsInRoom = actors.filter((actor) => {
                const device = devices.find((d) => d.id === actor.deviceId);

                return device && isRemoteDeviceOfType(device.roles, RemoteDeviceType.FHT_CHANNEL);
              });

              const climateAndCo2SensorinRoom = actors.filter((actor) => {
                const device = devices.find((d) => d.id === actor.deviceId);

                return (
                  device &&
                  (isRemoteDeviceOfType(device.roles, RemoteDeviceType.AIR_QUALITY_SENSOR) ||
                    isRemoteDeviceOfType(device.roles, RemoteDeviceType.CS))
                );
              });

              return climateAndCo2SensorinRoom.length >= 2 || !channelsInRoom.length;
            }
          }
        }

        return true;
      }
    );
  }

  export function getAmountOfDeviceOfTypeAssignedToRoom(
    roomFeatureName: string,
    deviceType: RemoteDeviceType
  ): MemoizedSelector<AppState, number, DefaultProjectorFn<number>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<RoomProperties, RoomCommands>(
        roomFeatureName,
        DeviceIds.RoomControl
      ),
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        roomFeature: Feature<RoomProperties, RoomCommands>
      ) => {
        let devicesInRoom = [];

        if (isReady(roomFeature)) {
          const actors = roomFeature?.properties?.actors?.value;
          if (actors && actors.length) {
            const devices = GatewaySelectors.getZigbeeDevices(gatewayDevices).devices;

            devicesInRoom = actors.filter((actor) => {
              const device = devices.find((d) => d.id === actor.deviceId);

              return device && isRemoteDeviceOfType(device.roles, deviceType);
            });
          }
        }

        return devicesInRoom.length;
      }
    );
  }

  export function selectConsolidatedValues(
    featureName: string,
    deviceId: string,
    consolidatedDeviceId: string
  ): MemoizedSelector<AppState, ConsolidatorDevice, DefaultProjectorFn<ConsolidatorDevice>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        featureName,
        consolidatedDeviceId
      ),
      (feature: Feature<ConsolidatedFeature, unknown>) => {
        let properDevice: ConsolidatorDevice;
        feature?.properties?.devices?.value?.forEach((device: ConsolidatorDevice) => {
          if (device?.id == deviceId) {
            properDevice = device;
          }
        });

        return properDevice;
      }
    );
  }

  export function getAddActorConstraints(
    roomFeature: Feature<RoomProperties, RoomCommands>,
    deviceId: string
  ): MemoizedSelector<AppState, AddActorConstraints, DefaultProjectorFn<AddActorConstraints>> {
    return createSelector(
      DevicesSelectors.isStandaloneDevice,
      InstallationFeaturesSelectors.getAmountOfDeviceOfTypeAssignedToRoom(
        roomFeature.feature,
        RemoteDeviceType.CS
      ),
      InstallationFeaturesSelectors.getAmountOfDeviceOfTypeAssignedToRoom(
        roomFeature.feature,
        RemoteDeviceType.AIR_QUALITY_SENSOR
      ),
      GatewaySelectors.isDeviceOfType(deviceId, RemoteDeviceType.FHT_CHANNEL),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        IsNumberValueProperty,
        DeviceHeatingCircuitIdCommands
      >(FeatureNames.deviceHeatingCircuitId, getFhtMainFromChannel(deviceId)),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<DeviceConfigProperty, unknown>(
        FeatureNames.deviceConfiguration,
        roomFeature.deviceId,
        roomFeature.gatewayId
      ),
      (
        isStandalone,
        csAssigned,
        co2Assigned,
        isFhtChannel,
        fhtMainHcId,
        deviceConfigurationFeature
      ) => {
        const heatingCircuitEnabled =
          deviceConfigurationFeature?.properties?.circuitsEnabled?.value?.length;

        if (isFhtChannel && !csAssigned && !co2Assigned) {
          return AddActorConstraints.NO_CS_OR_CO2_FOR_CHANNEL;
        } else if (
          heatingCircuitEnabled &&
          isFhtChannel &&
          fhtMainHcId.properties.value === undefined &&
          !isStandalone
        ) {
          return AddActorConstraints.NO_HC_FOR_CHANNEL;
        }
      }
    );
  }

  export function isDeviceAssigned(
    deviceId: string
  ): MemoizedSelector<AppState, { assigned: boolean }, DefaultProjectorFn<{ assigned: boolean }>> {
    return createSelector(
      InstallationFeaturesSelectors.getRoomsWithActors(),
      (roomsWithActors: Feature<RoomProperties, RoomCommands>[]) => {
        const actors = flatten(
          roomsWithActors.map((room) => room?.properties?.actors?.value || [])
        );

        return { assigned: !!actors.find((actor) => actor.deviceId === deviceId) };
      }
    );
  }

  export function getDevicesOfChosenTypeAssignedToTheRoom(
    roomId: string,
    type: RemoteDeviceType
  ): MemoizedSelector<AppState, RoomActor[], DefaultProjectorFn<RoomActor[]>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<RoomProperties, RoomCommands>(
        FeatureNames.roomDetails(roomId),
        DeviceIds.RoomControl
      ),
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        roomFeature: Feature<RoomProperties, RoomCommands>
      ) => {
        const roomActors = roomFeature?.properties?.actors?.value?.map((actor) => {
          if (GatewaySelectors.getIsDeviceOfType(actor.deviceId, type, gatewayDevices)) {
            return actor;
          }
        });

        return roomActors?.filter((actor) => actor) || [];
      }
    );
  }

  export function getAssignedDeviceIds(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.getRoomsWithActors(),
      (roomsWithActors: Feature<RoomProperties, RoomCommands>[]) => {
        const assignedDeviceIds: string[] = [];
        roomsWithActors.forEach((roomFeature) => {
          roomFeature.properties.actors.value.forEach((actor) => {
            assignedDeviceIds.push(actor.deviceId);
          });
        });

        return assignedDeviceIds;
      }
    );
  }

  export function ventilationSensorReadingVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.ventilationSensorsTemperatureSupply
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.ventilationSensorsHumiditySupply
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.ventilationSensorsTemperatureOutside
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.ventilationSensorsHumidityOutdoor
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.ventilationSensorsTemperatureExhaust
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.ventilationSensorsHumidityExhaust
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.ventilationSensorsTemperatureExtract
      ),
      InstallationFeaturesSelectors.isInstallationDeviceFeatureReady(
        FeatureNames.ventilationSensorsHumidityExtract
      ),
      (
        isventilationSensorsTemperatureSupplyReady,
        isventilationSensorsHumiditySupplyReady,
        isventilationSensorsTemperatureOutsideReady,
        isventilationSensorsHumidityOutdoorReady,
        isventilationSensorsTemperatureExhaustReady,
        isventilationSensorsHumidityExhaustReady,
        isventilationSensorsTemperatureExtractReady,
        isventilationSensorsHumidityExtractReady
      ) => {
        return (
          isventilationSensorsTemperatureSupplyReady ||
          isventilationSensorsHumiditySupplyReady ||
          isventilationSensorsTemperatureOutsideReady ||
          isventilationSensorsHumidityOutdoorReady ||
          isventilationSensorsTemperatureExhaustReady ||
          isventilationSensorsHumidityExhaustReady ||
          isventilationSensorsTemperatureExtractReady ||
          isventilationSensorsHumidityExtractReady
        );
      }
    );
  }
  export const selectGetFeaturesRequestFinished = createSelector(
    installationFeatures,

    (state) => state.getFeaturesRequestFinished
  );
  export function getPVString(
    pvStringId: string
  ): MemoizedSelector<AppState, PvStringObject, DefaultProjectorFn<PvStringObject>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.photovoltaicStringIdElectricity(pvStringId)
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.photovoltaicStringIdPower(pvStringId)
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.photovoltaicStringIdVoltage(pvStringId)
      ),
      (
        electricityFeature: Feature<IsNumberValueProperty, never>,
        powerFeature: Feature<IsNumberValueProperty, never>,
        voltageFeature: Feature<IsNumberValueProperty, never>
      ) => {
        const pvStringIdLetter: string = PvStringIdLetter[pvStringId];

        const pvStringObject: PvStringObject = {
          pvStringIdLetter,
          electricityFeature,
          powerFeature,
          voltageFeature,
        };

        return pvStringObject;
      }
    );
  }

  export function getFixedRoomsFeatures(): MemoizedSelector<
    AppState,
    Feature<RoomProperties, RoomCommands>[],
    DefaultProjectorFn<Feature<RoomProperties, RoomCommands>[]>
  > {
    return createSelector(
      installationFeatures,
      InstallationFeaturesSelectors.getRoomsFeatures(),
      (state: InstallationFeaturesState, roomsFeatures) => {
        const rooms: Feature<RoomProperties, RoomCommands>[] = [];
        roomsFeatures.forEach((roomsFeature) => {
          if (
            roomsFeature.properties.name.value === '' &&
            roomsFeature.properties.type.value === RoomTypes.INVALID
          ) {
            const noValueRoom = state.rooms.find((room) => room.feature === roomsFeature.feature);

            if (!noValueRoom) {
              const fixedRoomFeature = {
                ...roomsFeature,
                properties: {
                  ...roomsFeature.properties,
                  name: {
                    ...roomsFeature.properties.name,
                    value: state.addRoom.name,
                  },
                  type: {
                    ...roomsFeature.properties.type,
                    value: state.addRoom.type,
                  },
                },
              };
              rooms.push(fixedRoomFeature);
            } else {
              rooms.push(noValueRoom);
            }
          } else {
            rooms.push(roomsFeature);
          }
        });

        return rooms;
      }
    );
  }

  export function selectRemoteDeviceType(
    featureName: string,
    deviceId: string
  ): MemoizedSelector<AppState, string> {
    return createSelector(
      GatewaySelectors.selectVitotrolDevices(),
      DevicesSelectors.getClimateSensorDevices(),
      selectInstallationDeviceFeature(featureName),
      (
        remoteControlDevices: RemoteDeviceDetails[],
        climateSensorDevices: RemoteDeviceDetails[],
        feature: Feature<VitotrolRemoteControllerProperties, VitotrolRemoteControllerCommands>
      ): string => {
        const devices = [...remoteControlDevices, ...climateSensorDevices];
        const device = feature?.properties?.deviceId?.value
          ? devices.find((dev) => dev?.device?.id === feature?.properties?.deviceId?.value)
          : devices.find(
              (dev) =>
                feature?.commands?.setZigbeeController?.params?.deviceId?.constraints?.enum?.includes(
                  dev?.device?.id
                ) &&
                (!deviceId || dev?.device?.id === deviceId)
            );

        const has100E = device?.device?.roles?.includes(DeviceRoles.RemoteControllerVitotrol100E);
        const has300E = device?.device?.roles?.includes(DeviceRoles.RemoteControllerVitotrol300E);
        const hasClimateSensor = device?.device?.roles?.includes(DeviceRoles.ClimateSensor);
        if (!device?.device?.roles || (has100E && has300E)) {
          return '';
        }

        if (has100E) {
          return VitotrolName.Vitotrol_100E;
        }

        if (has300E) {
          return VitotrolName.Vitotrol_300E;
        }

        if (hasClimateSensor) {
          return '+SRC.DEVICE.SMART_CS_GENERIC';
        }

        return '';
      }
    );
  }

  export function getPowerLimitationLabel(
    deviceId: string
  ): MemoizedSelector<
    AppState,
    PowerLimitationTooltip[],
    DefaultProjectorFn<PowerLimitationTooltip[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(
        FeatureNames.powerLimitationOverview
      ),
      (powerLimitFeature: Feature<PowerLimitationOverviewProperties, never>) => {
        const powerLimitValuesArr = powerLimitFeature?.properties?.limitations?.value;
        if (!powerLimitValuesArr) {
          return [];
        }
        const devicesFound = powerLimitValuesArr.filter((device) => device.deviceId === deviceId);
        if (devicesFound.length === 0) {
          return [];
        }

        const validDevicesFound = devicesFound.filter((device) => {
          return (
            typeof device.limitationType === 'string' &&
            device.limitationType !== '' &&
            typeof device.method === 'string' &&
            device.method !== ''
          );
        });

        return validDevicesFound.map((device) => {
          const label = `${device.limitationType}_${device.method}`;

          const { limitationType, value, unit } = device;

          const isConsumption = limitationType === PowerLimitationType.consumption;

          return { label, isConsumption, value, unit };
        });
      }
    );
  }

  export function selectIsShowPvVoltage(
    devices: RichDevice[]
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    const installationDeviceFeatureSelectors = devices.map(
      (device) =>
        InstallationFeaturesSelectors.selectInstallationDeviceFeature(
          FeatureNames.essSystemType,
          device.id
        ) as MemoizedSelector<AppState, Feature<IsStringValueProperty, never>>
    );

    return createSelector(
      ...installationDeviceFeatureSelectors,
      (...features: Feature<IsStringValueProperty, never>[]) => {
        const validSystemTypesToShowPvVoltage: string[] = [
          EssSystemType.hybrid,
          EssSystemType.photovoltaic,
        ];

        return features.some((feature) =>
          validSystemTypesToShowPvVoltage.includes(feature?.properties?.value?.value)
        );
      }
    );
  }

  export function selectIsSelfConsumptionOptimization(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationHemsFeature(
        FeatureNames.forecastBasedBatteryOperation
      ),
      InstallationFeaturesSelectors.selectInstallationHemsFeature(
        FeatureNames.photovoltaicExcessHeatpumpHandling
      ),
      InstallationFeaturesSelectors.selectInstallationHemsFeature(
        FeatureNames.photovoltaicExcessPvHandling
      ),
      (
        forecastBasedBatteryOperation: Feature<IsActiveProperty, ActivateDeactivateCommands>,
        photovoltaicExcessHeatpumpHandling: Feature<IsActiveProperty, ActivateDeactivateCommands>,
        photovoltaicExcessPvHandling: Feature<IsActiveProperty, ActivateDeactivateCommands>
      ) =>
        forecastBasedBatteryOperation?.properties?.active?.value ||
        photovoltaicExcessHeatpumpHandling?.properties?.active?.value ||
        photovoltaicExcessPvHandling?.properties?.active?.value
    );
  }

  export function selectCanAddVitotrolToInstallation(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      (feature: Feature<GatewayDevicesProperties, never> | null | undefined): boolean => {
        if (!feature) {
          return false;
        }

        const allowedTypes = [
          DeviceRoles.Heatpump,
          DeviceRoles.Vitodens100NA,
          DeviceRoles.Vitodens200NA,
          DeviceRoles.Vitovalor300,
          DeviceRoles.ViAir300,
        ];

        const deviceData = feature.properties.devices.value;

        return deviceData.some((datas) =>
          datas.roles.some((role: DeviceRoles) => allowedTypes.includes(role))
        );
      }
    );
  }

  export function selectIsHybridProTileVisible(
    deviceId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      selectInstallationDeviceFeature<SecondaryHeatGeneratorProperty, unknown>(
        FeatureNames.secondaryHeatGenerator,
        deviceId
      ),
      (feature) => feature?.properties?.active?.value
    );
  }
}
